import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { calTotal } from "../../../../../utils/giftshop";
import { RootState } from "../../../../../store";
import {
  EncryptionKeyResponse,
  PAYMENT_TYPE,
} from "../../../../../models/payment";
import CreditCard, {
  ICardRef,
} from "../../../../../components/TicketBooking/Payment/CreditCard";
import {
  getCreditCardEncryptionKey,
  getPaymentMethod,
  giftCardCompleteOrderApi,
  giftCardMakePaymentApi,
  giftCardOrderContinueAPI,
  refundGiftCardPaymentApi,
  sendMailApi,
} from "../../../../../services/payment";
import {
  BRANDS,
  CAPTCHA_SITE_KEY,
  COUNTRY,
  GIFT_CARD_CINEMA_ID,
  PAYMENT_PROVIDERS,
} from "../../../../../constants/app";
import { ICartData } from "../../../../../store/giftshop/type";
import { GiftType, PROMO_TYPE } from "../../../../../models/giftcard";
import { addGiftCart, updateGiftCardBookingIdAction } from "../../../../../store/giftshop/action";
import { TICKET_FLOW } from "../../../../../constants/ticket";
import { MODAL_TYPE, TICKET_FLOW_TYPES } from "../../../../../models/tickets";
import { setModal, toggleTicketModal } from "../../../../../store/ticketBooking/action";
import { COUNTRY_IMAGE_CONFIG, IMAGE_URL_CONFIG } from "../../../../../constants/url";
import {
  GIFTSHOP_MAKE_PAYMENT_REQUEST_TYPE,
  GIFT_SHOP_REFUND_REQUEST_TYPE,
  GIFT_SHOP_TOKEN_PAYMENT_REQUEST_TYPE,
} from "../../../../../constants/payment";
import { encryptData } from "../../../../../utils/helper";
import StripePayment from "../../../../../components/TicketBooking/Payment/Stripe";
import ReCAPTCHA from "react-google-recaptcha";
import "./payment.scss";
import GPay from "../../../../../components/TicketBooking/Payment/GPay";
import ApplePay from "../../../../../components/TicketBooking/Payment/GPay";
import { setContentModal, toggleContentModal } from "../../../../../store/application/action";
import Button from "../../../../../components/UI/Button";

let sendEmail = "";

interface IGiftshopPaymentProps {}

const GiftshopPayment: FC<IGiftshopPaymentProps> = () => {
  const {
    cart,
    orderResponse,
    userDetails,
    memberDetail,
    countryId,
    isLoggedIn,
    brandId,
    currentCinema
  } = useSelector((state: RootState) => ({
    cart: state.giftCardReducer.cart,
    orderResponse: state.giftCardReducer.orderResponse,
    userDetails: state.authReducer.userDetails,
    memberDetail: state.authReducer.memberDetail,
    countryId: state.applicationReducer.countryId as COUNTRY,
    isLoggedIn: state.authReducer.isLoggedIn,
    brandId: state.applicationReducer.brandId,
    currentCinema: state.applicationReducer.currentCinema,
  }));


  const dispatch = useDispatch();

  const [paymentType, setPaymentType] = useState(PAYMENT_TYPE.CARD);
  const [errorMessage, setErrorMessage] = useState("");
  const [googlePaySupport, setGooglePaySupport] = useState(false);
  const cardRef = useRef<ICardRef>(null);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [errorEmailAddress, setErrorEmailAddress] = useState("");
  const [showWallet, setShowWallet] = useState("");
  const [cartData, setCartData] = useState<{
    deliveryFee: number;
    deliveryQty: number;
    total: number;
    shippingAddress: Array<any>;
    deliveryCharge: number;
  }>({ deliveryFee: 0, deliveryQty: 0, total: 0, deliveryCharge: 0, shippingAddress:[]});

  useEffect(() => {
    if (
      countryId !== COUNTRY.NZ &&
      !/^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    ) {
      setShowWallet(PAYMENT_TYPE.GOOGLE_PAY);
    } else if (
      countryId !== COUNTRY.NZ &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    ) {
      const applepaySession = (window as any).ApplePaySession;
      if (applepaySession) {
        if (applepaySession.canMakePayments()) {
          setShowWallet(PAYMENT_TYPE.APPLE_PAY);
        }
      }
    }
  }, [countryId]);

  useEffect(() => {
    setCartData(calTotal(cart, countryId));
  }, [cart]);

  const totalAmount = useMemo(() => {
    const { total } = calTotal(cart, countryId);
    return total;
  }, [cart]);

  const promoAmount = useMemo(() => {
    return cart.reduce((total: number, v: any) => {
      return (total =
        total + ((v.giftcardType === GiftType.ecard && v.promoType === PROMO_TYPE.SEPARATE)? v.promo.amount : 0));
    }, 0);
  }, [cart]);

  const makeGiftCardPayment = async (
    processPayload: any,
    ptype: PAYMENT_TYPE,
    key: string
  ) => {
    //Make payment

    let amount = totalAmount;
    if (getPaymentMethod(countryId) !== PAYMENT_PROVIDERS.STRIPE) {
      amount = +(+amount * 100).toFixed();
    }

    sendEmail = processPayload?.cardDetails?.email
      ? processPayload.cardDetails.email
      : ptype === PAYMENT_TYPE.APPLE_PAY || ptype === PAYMENT_TYPE.GOOGLE_PAY
      ? emailAddress
      : userDetails?.email!;

    const makePaymentPayload: any = {
      msgId: orderResponse?.userSessionId!,
      cinemaId: brandId === BRANDS.AU?GIFT_CARD_CINEMA_ID[countryId]:currentCinema.slug,
      amount: amount,
      transactionReference: `invoice${orderResponse?.userSessionId!}`,
      requestType: GIFTSHOP_MAKE_PAYMENT_REQUEST_TYPE[countryId],
      saleId: orderResponse?.userSessionId!,
      email: sendEmail,
      countryId: countryId,
      paymentSessionId: orderResponse?.userSessionId!,
      memberRefId: userDetails?.result?.memberRefId || "",
    };

    //Checking payment type - new card, saved card, apple pay or google pay
    if (
      (processPayload.cardDetails && processPayload.action === "newCard") ||
      ptype === PAYMENT_TYPE.APPLE_PAY ||
      ptype === PAYMENT_TYPE.GOOGLE_PAY
    ) {
      const encryptKey = key + orderResponse?.userSessionId!;

      const encryptedData = encryptData(
        JSON.stringify(processPayload.cardDetails),
        encryptKey
      );
      makePaymentPayload.data = encryptedData;
      makePaymentPayload.saveCreditCard = processPayload?.saveCard;
    } else if (
      processPayload.cardDetails &&
      processPayload.action === "savedCard"
    ) {
      makePaymentPayload.tokenValue = processPayload.cardDetails.token;
      makePaymentPayload.requestType =
        GIFT_SHOP_TOKEN_PAYMENT_REQUEST_TYPE[countryId];
    }

    if (ptype === PAYMENT_TYPE.APPLE_PAY || ptype === PAYMENT_TYPE.GOOGLE_PAY) {
      makePaymentPayload.googleApplePay = true;
      makePaymentPayload.id = processPayload.paymentIntentId;
    }

    return await giftCardMakePaymentApi(makePaymentPayload);
  };

  const completeOrder = async ({ cardType }: any) => {
    let amt_cent = (+totalAmount) * 100;
    let amt = amt_cent.toFixed();

    let vistaTotal = cart
      .filter((item: ICartData) => item.giftcardType == GiftType.ecard)
      .reduce(
        (a: number, b: ICartData) =>
          a + b.quantity * (!b.isVariablePriceItem ? b.price : b.giftamount),
        0
      );
    let vistaAmt = (+vistaTotal+promoAmount) * 100;

    let completeOrderPayload: any = {
      requestType: "completeOrder",
      userSessionId: orderResponse?.userSessionId!,
      firstName: userDetails?.firstName,
      lastName: userDetails?.lastName,
      email: sendEmail,
      phoneNumber: userDetails?.phoneNumber,
      paymentCardType: cardType,
      paymentAmountInCents: parseInt(amt),
      saleId: orderResponse?.userSessionId!,
      paymentSessionId: orderResponse?.userSessionId!,
      loyaltyCardNumber:
        memberDetail &&
        memberDetail?.LoyaltyMember &&
        memberDetail?.LoyaltyMember?.CardNumber
          ? memberDetail.LoyaltyMember.CardNumber
          : "",
      countryId: countryId,
      eGiftAmount: parseInt(vistaAmt.toFixed()),
      paidamount: totalAmount,
      termsCheck: true, // Terms and conditions
    };

    return await giftCardCompleteOrderApi(completeOrderPayload);
  };

  const refundCard = async (
    txnID: string,
    transactionReference: string,
    amount: number
  ) => {
    const refundDetails = {
      cinemaId: brandId === BRANDS.AU?GIFT_CARD_CINEMA_ID[countryId]:currentCinema.slug,
      amount: amount,
      countryId: countryId,
      txnType: 4,
      requestType: GIFT_SHOP_REFUND_REQUEST_TYPE[countryId],
      paymentMethod: "ccard",
      txnID: txnID,
      transactionReference: transactionReference,
      saleId: orderResponse?.userSessionId!,
    };
    return await refundGiftCardPaymentApi(refundDetails);
  };

  const sendConfirmationMails = async () => {
    let payloadDataConfirmation = {
      id: orderResponse?.userSessionId!,
      templateName: "Giftcard order confirmation",
      email: sendEmail,
      countryId: countryId,
    };
    await sendMailApi(payloadDataConfirmation);
    let payloadDataCard = {
      countryId: countryId,
      id: orderResponse?.userSessionId!,
      templateName: "GiftcardShop send",
    };
    await sendMailApi(payloadDataCard);
    // Sending mail if physical cards purchased
    const phyCards = cart.filter(
      (item: ICartData) => item.giftcardType != GiftType.ecard
    );
    if (phyCards.length > 0) {
      let physicalCard = {
        id: orderResponse?.userSessionId!,
        templateName: "Physical Card Booking",
        countryId: countryId,
      };
      await sendMailApi(physicalCard);
    }
    return true;
  };

  const paymentError = () => {
    const modal =
      TICKET_FLOW[TICKET_FLOW_TYPES.GIFTSHOP][MODAL_TYPE.GIFTSHOP_ERROR];
    dispatch(
      setModal({
        ...modal,
        type: TICKET_FLOW_TYPES.GIFTSHOP,
      })
    );
  };

  const processPayment = async (processPayload: any, ptype: PAYMENT_TYPE) => {
    getCreditCardEncryptionKey(
      {
        countryId: countryId,
        saleId: orderResponse?.userSessionId!,
        requestType: "giftcard",
      },
      async (data: EncryptionKeyResponse | null) => {
        if (data) {
          //orderContinue api
          await giftCardOrderContinueAPI({
            userSessionId: orderResponse?.userSessionId!,
            requestType: "orderContinue",
            saleId: orderResponse?.userSessionId!,
            countryId: countryId,
          });
          const makePaymentResponse: any = await makeGiftCardPayment(
            processPayload,
            ptype,
            data.data.key
          );
          if (makePaymentResponse.status) {
            const completeOrderResponse: any = await completeOrder({
              cardType: "ccard",
            });

            if (
              completeOrderResponse.status &&
              completeOrderResponse.VistaBookingId
            ) {
              dispatch(
                updateGiftCardBookingIdAction(
                  completeOrderResponse.VistaBookingId
                )
              );
              const result: any = await sendConfirmationMails();
              if (result) {
                const modal =
                  TICKET_FLOW[TICKET_FLOW_TYPES.GIFTSHOP][
                    MODAL_TYPE.GIFTSHOP_CONFIRMATION
                  ];
                dispatch(
                  setModal({
                    ...modal,
                    type: TICKET_FLOW_TYPES.GIFTSHOP,
                    data: {
                      email: sendEmail,
                      giftcardDetails: completeOrderResponse.giftcardDetails
                    }
                  })
                );
              }
            } else {
              const result = await refundCard(
                makePaymentResponse.data.txnID,
                makePaymentResponse.data.transactionReference,
                makePaymentResponse.data.amount
              );
              if (result) {
                paymentError();
              }
            }
          } else {
            paymentError();
          }
        }
      }
    );
  };

  const onCheckTerms = (event: any) => {
    const {
      target: { checked },
    } = event;
    setIsTermsChecked(checked);
  };

  const onChangePaymentType = async (type: PAYMENT_TYPE) => {
    setPaymentType(type);
    if (type === PAYMENT_TYPE.APPLE_PAY || type === PAYMENT_TYPE.GOOGLE_PAY) {
      if (!emailAddress) {
        setErrorEmailAddress("Email is required");
        return;
      } else {
        setErrorEmailAddress("");
      }

    } else {
      setEmailAddress("");
      setErrorEmailAddress("");
    }

    switch (type) {
      case PAYMENT_TYPE.APPLE_PAY:
        // applePay();
        break;
      case PAYMENT_TYPE.GOOGLE_PAY:
        // googlePay();
        break;
    }
  };

  const onCardPayment = async () => {
    const cardDetails: any = cardRef?.current?.submit();
    if (!cardDetails) {
      return;
    }
    const { card, action, saveCard } = cardDetails;

    if (!isCaptchaChecked) {
      setErrorMessage("Verification Code is required");
      return;
    }

    if (action === "newCard") {
      processPayment(
        { cardDetails: card, action, saveCard },
        PAYMENT_TYPE.CARD
      );
    } else if (action === "savedCard") {
      processPayment({ cardDetails: card, action }, PAYMENT_TYPE.CARD);
    }
  };

  const onPurchase = () => {
    switch (paymentType) {
      case PAYMENT_TYPE.CARD:
        onCardPayment();
        break;
    }
  };
  const getTerms = () => {
    // API_CALLER.get(pagecontent, {
    //   params: {
    //     countryId: `${COUNTRYID}`,
    //     pageName: "etixterms",
    //   },
    // }).then(async (response: any) => {
    //   if (response.status === 200) {
    //     navigatorUtil.push("PdfViewer", {
    //       pageContent: response.data.data,
    //       pageType: "Terms",
    //       pageTitle: "e-Tix Terms and conditions",
    //     });
    //   }
    // });
  };

  const toggleTab = (type: PAYMENT_TYPE) => {
    setErrorMessage("");
    setPaymentType(type);
  };

  const onChangeCaptcha = (event: any) => {
    if (event) {
      setIsCaptchaChecked(true);
      setErrorMessage("");
    } else {
      setIsCaptchaChecked(false);
      setErrorMessage("Verification Code is required");
    }
  };

  const checkValid = () => {
    if (!isCaptchaChecked) {
      setErrorMessage("Verification Code is required");
      return false;
    }
  };

  const isValid = useMemo(() => {
    if (paymentType === PAYMENT_TYPE.GOOGLE_PAY) {
      if (!isCaptchaChecked) {
        return false;
      }
    }
    setErrorMessage("");
    setErrorMessage("");
    return true;
  }, [ isCaptchaChecked, paymentType]);

  const onError = (errorMsg?: string) => {
    const modal =
      TICKET_FLOW[TICKET_FLOW_TYPES.GIFTSHOP][MODAL_TYPE.GIFTSHOP_ERROR];
    dispatch(
      setModal({
        ...modal,
        type: MODAL_TYPE.GIFTSHOP_ERROR,
      })
    );
  };

  const openTerms = () => {
    dispatch(
      setContentModal({
        type: "Gift Shop Terms",
      })
    );
    dispatch(toggleContentModal(true));
  };

  const onCancelTicket = () => {
    dispatch(toggleTicketModal(false));
  };


  return (
    <div className="row giftcard-payment-wrapper">
      <div className="col-12 auth-wrapper px-4">
        <div className="row">
          <div className="col-md-12">
            <div className="row no-gutters pay-method">
              <div className="col-12 col-md-12">
                <div className="form_page pay_heading">
                  <h4 className="mb-4">Payment Details</h4>
                  <div className="pay_method_icons">
                    <ul className="nav nav-tabs">
                      <li
                        className={`nav-item icons ${
                          paymentType === PAYMENT_TYPE.CARD ? "active" : ""
                        }`}
                        onClick={() => toggleTab(PAYMENT_TYPE.CARD)}
                      >
                        <a
                          className="nav-link active"
                        >
                          <span className="credit_card ">Credit Card</span>
                        </a>
                      </li>
                      {/* {showWallet === PAYMENT_TYPE.GOOGLE_PAY ? (
                        <li
                          className={`nav-item icons ${
                            paymentType === PAYMENT_TYPE.GOOGLE_PAY
                              ? "active"
                              : ""
                          }`}
                          onClick={() => toggleTab(PAYMENT_TYPE.GOOGLE_PAY)}
                        >
                          <a className="nav-link">
                            <span className="wallet_pay gpay_cls">
                              <img
                                alt="GPayIcon"
                                className="gpayBtn"
                                src={URLS.GOOGLE_PAY_MARK}
                              />
                            </span>
                          </a>
                        </li>
                      ) : null}
                      {showWallet === PAYMENT_TYPE.APPLE_PAY ? (
                        <li
                          className={`nav-item icons ${
                            paymentType === PAYMENT_TYPE.APPLE_PAY
                              ? "active"
                              : ""
                          }`}
                          onClick={() => toggleTab(PAYMENT_TYPE.APPLE_PAY)}
                        >
                          <a className="nav-link">
                            <span className="applePay_cls">
                              <img
                                src={URLS.APPLE_PAY_ICON}
                                alt="ApplePayIcon"
                              />{" "}
                              Apple Pay
                            </span>
                          </a>
                        </li>
                      ) : null} */}
                    </ul>
                  </div>
                </div>
              </div>
              <div
                className={`${
                  paymentType === PAYMENT_TYPE.CARD ? "d-md-flex" : ""
                }`}
              >
                <div
                  className={`col-12 ${
                    paymentType === PAYMENT_TYPE.CARD ? "col-md-6" : ""
                  }`}
                >
                  {paymentType === PAYMENT_TYPE.CARD ? (
                    <CreditCard ref={cardRef} fromPage="giftshop" />
                  ) : null}
                </div>
                <div
                  className={`col-12 ${
                    paymentType === PAYMENT_TYPE.CARD ? "col-md-6 ml-40" : ""
                  }`}
                >
                  <div className="col-12 mt-4 d-none">
                    <div className="custom_checkbox option_link">
                      <input
                        type="checkbox"
                        value="1"
                        checked={isTermsChecked}
                        onClick={onCheckTerms}
                      />
                      <div className="state p-primary">
                        <label className="form-label">
                          <div>
                          {countryId === COUNTRY.STA ? (
                        <>
                          <span>
                            I have read and understand the State Cinema Internet
                            Ticket Sales {" "}
                          </span>
                          <span className="non_atag_link_u" onClick={openTerms}>
                            Terms and conditions
                          </span>
                        </>
                      ) : countryId === COUNTRY.ANG ? (
                        <>
                          <span>
                            I have read and understand the Angelika Internet
                            Ticket Sales {" "}
                          </span>
                          <span className="non_atag_link_u" onClick={openTerms}>
                            Terms and conditions
                          </span>
                        </>
                      ) : brandId === BRANDS.US ? (
                        <>
                          <span>I have read & understand the{" "}</span>
                          <span className="non_atag_link_u" onClick={openTerms}>
                            Terms & Conditions
                          </span>
                        </>
                      ) : (
                        <>
                          <span>
                            I have read and understand the Reading Internet
                            Ticket Sales{" "}
                          </span>
                          <span className="non_atag_link_u" onClick={openTerms}>
                            Terms and conditions
                          </span>
                        </>
                      )}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row mt-4">
                    <div className="col-12  col-md-12">
                      <label className="form-label">Verification Code* </label>
                      <div className="form-group">
                        <ReCAPTCHA
                          sitekey={`${CAPTCHA_SITE_KEY}`}
                          onChange={onChangeCaptcha}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="payform-total">
                    <label>TOTAL PURCHASE</label>
                    <h3>${calTotal(cart, countryId).total.toFixed(2)}</h3>
                    {cart.map((v: ICartData) => (
                      <p>
                        {v.quantity} X
                        {v.description} : ${" "}
                        {(
                          v.quantity *
                          (v.isVariablePriceItem ? v.giftamount : v.price)
                        ).toFixed(2)}
                      </p>
                    ))}
                    {cartData.deliveryQty > 0 ? (
                      brandId === BRANDS.AU?<p>
                        {cartData.deliveryQty} X ${cartData.deliveryCharge}{" "}
                        DELIVERY FEE: ${cartData.deliveryFee.toFixed(2)}
                      </p>:<p>
                        DELIVERY FEE: ${cartData.deliveryFee.toFixed(2)}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
              {errorMessage ? (
                <div className="error_message text-center">
                  <span>{errorMessage}</span>
                </div>
              ) : null}
              <div className="col-md-12 movie_footer_btn_el">
                <div className="movie_button_wrap">
                  <Button classNames="m-r-10" outlineIcon={`${COUNTRY_IMAGE_CONFIG[countryId]}left_arrow.svg`} iconPosition = "pre" outlineHovericon={`${IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH}white-left-arrow.svg`} iconWidth={15} iconHeight={15} onClick={onCancelTicket} text= "Back" type="outline"  /> 
                  {paymentType === PAYMENT_TYPE.APPLE_PAY &&
                  countryId !== COUNTRY.NZ ? (
                    <ApplePay
                      processPayment={processPayment}
                      isValid={isValid}
                      onError={onError}
                      checkValid={checkValid}
                      page={"giftcard"}
                    />
                  ) : paymentType === PAYMENT_TYPE.GOOGLE_PAY &&
                    countryId !== COUNTRY.NZ ? (
                    <GPay
                      processPayment={processPayment}
                      isValid={isValid}
                      onError={onError}
                      checkValid={checkValid}
                      page={"giftcard"}
                    />
                  ) : (paymentType === PAYMENT_TYPE.APPLE_PAY ||
                      paymentType === PAYMENT_TYPE.GOOGLE_PAY) &&
                    countryId === COUNTRY.NZ ? (
                    <StripePayment
                      processPayment={processPayment}
                      isValid={isValid}
                      onError={onError}
                      checkValid={checkValid}
                    />
                  ) : (
                    <Button iconWidth={15} iconHeight={15} onClick={onPurchase} text= "Purchase" type="fill" iconPosition = "pre" icon={`${IMAGE_URL_CONFIG.DEFAULT_IMAGE_PATH}tick-white.svg`} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftshopPayment;
